import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { t } from "../../translations";

import "../styles/section-cart.css";

import { MdOutlineArrowBackIosNew } from "react-icons/md";

import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { POST_CART_TOTALS, POST_USER_DATA } from "../../store/action";
import ContributionComponent from "./contribution-component";

import { GET, POST, UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { Aa } from "../functions/Aa";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import { openNotificationWithIcon } from "./notification-bar";

import { CircularProgress } from "@mui/material";
import { getStoredUserData, hasStoredAuth } from "../../utils/authStorageUtils";
import { validateDeliveryBoundary } from "../../utils/deliveryBoundaryValidator";
import { translateErrorMessage } from "../../utils/errorMessageTranslator";
import { createLogger } from "../../utils/logger";
import { getRefreshToken } from "../../utils/tokenUtils";
import AddressItem from "./AddressItem";
import AddressModal from "./AddressModal";

const logger = createLogger("SectionCart");
const SectionCart = (props) => {
  // Debug: Track component re-renders
  logger.debug("🔄 SectionCart component re-rendered");

  // Get selectedChef directly from Redux state (not from props)
  const selectedChef = useSelector((state) => state.selectedChef);

  // Combined Redux selectors to reduce re-renders
  const { cart, selectedWhen, lang, userData } = useSelector((state) => ({
    cart: state.cart,
    selectedWhen: state.selectedWhen,
    lang: state.lang,
    userData: state.user
  }), shallowEqual);

  // Initialize dispatch hook
  const dispatch = useDispatch();

  // Debug: Log Redux user data when essential fields change (optimized)
  const userDataDebugInfo = useMemo(() => ({
    hasUserData: !!userData,
    userId: userData?._id,
    hasRefreshToken: !!userData?.refresh_token,
    refreshTokenLength: userData?.refresh_token?.length
  }), [userData?._id, userData?.refresh_token]);

  useEffect(() => {
    logger.log("📊 Redux User Data in SectionCart:", {
      ...userDataDebugInfo,
      refreshTokenValue: userData?.refresh_token,
      userDataKeys: userData ? Object.keys(userData) : [],
      userDataSample: userData
    });
  }, [userDataDebugInfo, userData]);

  // Monitor selectedChef changes to debug staging issues
  useEffect(() => {
    logger.log("👨‍🍳 selectedChef state in SectionCart:", {
      hasSelectedChef: !!selectedChef,
      chefId: selectedChef?._id,
      chefName: selectedChef ? `${selectedChef.firstname} ${selectedChef.lastname}` : 'No chef',
      source: 'redux'
    });
    
    // Log warning if selectedChef is missing (error will be thrown in checkout function)
    if (!selectedChef) {
      logger.warn("⚠️ WARNING: selectedChef is missing from Redux state", {
        reduxState: { selectedChef: null }
      });
    }
  }, [selectedChef]);

  // Use cart service
  const { decreaseQuantity, increaseQuantity } = useCart();

  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState(false);
  
  // Debounce ref for checkout button
  const checkoutDebounceRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Default max delivery distance (can be fetched from settings if needed)
  const MAX_DELIVERY_DISTANCE = 30; // km

  // Get cart totals from Redux with default values
  const {
    cartTotal = 0,
    tip = 0,
    deliveryFee: deliveryFeeState = 0,
    tax = 0,
    serviceFee = 0,
    promoValue: promoValueState = 0,
    total = 0,
    totalWithPromo = 0
  } = useSelector((state) => state.cartTotals) || {};

  // const [selectedTip, setSelectedTip] = useState(null);

  let history = useHistory();

  const {
    deliveryDetail,
    setDeliveryDetail,
    deliveryFee,
    promo_code,
    setPromoCode,
    promo_value,
    setPromoValue,
    province_detail,
    custom_help,
    setCustomHelp
  } = props;

  const getDiscount = async (code) => {
    if (code.length === 0) {
      return;
    }
    setLoad(true);

    const request_details = {
      type: GET,
      url: service_api,
      route: `discount/get-one?code=${code.toLowerCase()}`,
      data: null,
      token: userData?.refresh_token
    };

    try {
      const response = await MakeRequestAsync(request_details);
      const decryptData = decryptSingleData(response.data.discount);
      return setPromoValue(decryptData.value);
    } catch (err) {
      logger.log(err);
      return openNotificationWithIcon("error", t("validation.this_coupon_is_"));
    } finally {
      setLoad(false);
    }
  };

  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    if (!selectedWhen) {
      setCurrentDate("");
      return;
    }

    const options = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    // Parse the date string properly to avoid timezone issues
    // If selectedWhen is "2026-01-17", we want to display January 17, 2026
    let dateToFormat;
    if (typeof selectedWhen === 'string') {
      // Parse as YYYY-MM-DD format and treat as local date
      const [year, month, day] = selectedWhen.split('-');
      dateToFormat = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      dateToFormat = new Date(selectedWhen);
    }

    const formattedDate = dateToFormat.toLocaleDateString(
      undefined,
      options
    );
    setCurrentDate(formattedDate);
    
    logger.log("Cart - selectedWhen:", selectedWhen);
    logger.log("Cart - formattedDate:", formattedDate);
  }, [selectedWhen]);

  // Memoized calculations to prevent unnecessary re-computations
  const calculationResults = useMemo(() => {
    const newCartTotal = cart?.reduce(
      (a, b) => a + (parseFloat(b?.cost) || 0) * (parseFloat(b?.qty) || 0),
      0
    );

    // Calculate tip based on new system
    let newTip = 0;
    if (props?.selectedTip?.startsWith("percentage_")) {
      const percentage = parseFloat(props.selectedTip.split("_")[1]);
      newTip = (newCartTotal * percentage) / 100;
    } else if (props?.selectedTip === "custom") {
      newTip = parseFloat(custom_help) || 0;
    }

    // Use backend-calculated delivery fee
    const backendDeliveryFee = parseFloat(deliveryFee) || 0;
    const taxRate = parseFloat(province_detail?.tax) || 0;
    const newTax = (newCartTotal * taxRate) / 100;
    const newServiceFee = 3.99; // Fixed service fee
    const newPromoValue = parseInt(promo_value) || 0;

    const newTotal =
      newCartTotal + newTip + backendDeliveryFee + newTax + newServiceFee;
    const newTotalWithPromo = newTotal - newPromoValue;

    return {
      cartTotal: newCartTotal,
      tip: newTip,
      deliveryFee: backendDeliveryFee,
      totalWithPromo: newTotalWithPromo,
      tax: newTax,
      serviceFee: newServiceFee,
      promoValue: newPromoValue,
      total: newTotal
    };
  }, [
    cart,
    props?.selectedTip,
    custom_help,
    deliveryFee,
    province_detail?.tax,
    promo_value
  ]);

  // Update Redux state when calculations change
  useEffect(() => {
    dispatch({
      type: POST_CART_TOTALS,
      data: calculationResults
    });
  }, [calculationResults, dispatch]);

  // Validate a single address
  const validateAddress = (addressIndex,storedData) => {
    if (!userData?.address?.[addressIndex]) {
      return null;
    }

    if (selectedChef?.restaurant) {
      // For restaurants, validate distance against the restaurant's own location
      return validateDeliveryBoundary(
        selectedChef,
        userData.address[addressIndex],
        MAX_DELIVERY_DISTANCE
      );
    }

    if (!selectedChef.selectedHub) {
      return null;
    }

    return validateDeliveryBoundary(
      selectedChef.selectedHub,
      userData.address[addressIndex],
      MAX_DELIVERY_DISTANCE
    );
  };

  const post_user_data = (data) => {
    dispatch(post_user(data));
  };


  // Address handling moved to AddressModal component for better state isolation

  const loadPaymentPage = async () => {
    // Prevent multiple simultaneous calls
    if (isProcessingRef.current) {
      logger.debug("[WAIT] Checkout already in progress, ignoring duplicate call");
      return;
    }
    
    isProcessingRef.current = true;
    try {
      // Validate selectedChef exists
      if (!selectedChef || !selectedChef._id) {
        throw new Error("selectedChef is required for checkout but is missing or invalid");
      }

      logger.log("?? ==> CHECKOUT PROCESS STARTED <==");
    
      // Debug: Check Redux user data
      logger.log("?? Redux State Check:", {
        hasUserData: !!userData,
        userId: userData?._id,
        hasRefreshTokenInRedux: !!userData?.refresh_token,
        userDataKeys: userData ? Object.keys(userData) : [],
        userDataSample: userData
      });

      // Debug: Check localStorage directly
      logger.log("?? LocalStorage Check:");
      const hasStored = hasStoredAuth();
      const storedData = getStoredUserData();
      logger.log("?? Storage Results:", {
        hasStoredAuth: hasStored,
        hasStoredData: !!storedData,
        storedUserId: storedData?._id,
        hasStoredToken: !!storedData?.refresh_token,
        storedTokenLength: storedData?.refresh_token?.length
      });

      // Checkout requires authentication - check and fetch token if needed
      logger.log("?? Attempting to get refresh token...");
      const refreshToken = await getRefreshToken(userData, post_user_data);
      
      logger.log("?? Final Auth Check Result:", {
        hasUserId: !!userData?._id,
        hasRefreshToken: !!refreshToken,
        refreshTokenValue: refreshToken,
        refreshTokenLength: refreshToken?.length,
        authCheckPassed: !!(userData?._id && refreshToken)
      });
      
      if (!userData?._id || !refreshToken) {
        logger.error("? CHECKOUT AUTHENTICATION FAILED:", {
          missingUserId: !userData?._id,
          missingRefreshToken: !refreshToken,
          userDataSnapshot: JSON.stringify(userData, null, 2),
          refreshTokenSnapshot: refreshToken
        });
        
        return openNotificationWithIcon(
          "error",
          t("errors.login_required_checkout", lang) ||
            "Please login using the navigation menu to proceed with checkout."
        );
      }
      
      logger.log("? Authentication passed, proceeding with checkout...");

      // Debug: Log address information (captured at execution time)
      logger.log("[DEBUG] Address Validation (from loadPaymentPage):", {
        hasUserData: !!userData,
        hasAddressProperty: !!userData?.address,
        addressCount: userData?.address?.length || 0,
        deliveryDetail,
        deliveryDetailType: typeof deliveryDetail,
        deliveryDetailSource: "props",
        propsDeliveryDetail: props?.deliveryDetail,
        addresses: userData?.address?.map((addr, idx) => ({
          index: idx,
          hasReceiverName: !!addr?.receiver_name,
          hasStreet: !!addr?.street_name,
          hasPosition: !!addr?.position
        }))
      });

      if (!userData?.address || userData.address.length === 0) {
        logger.warn("⚠️ No addresses found in userData", {
          hasUserData: !!userData,
          addressCount: userData?.address?.length || 0,
          userDataKeys: userData ? Object.keys(userData) : []
        });
        return openNotificationWithIcon(
          "error",
          t("buttons.set_a_delivery__1", lang)
        );
      }

      // Validate that deliveryDetail index is valid
      if (deliveryDetail === null || deliveryDetail === undefined || 
          typeof deliveryDetail !== 'number' || 
          deliveryDetail < 0 || 
          deliveryDetail >= userData.address.length) {
        logger.warn("⚠️ Invalid deliveryDetail index", {
          deliveryDetail,
          deliveryDetailType: typeof deliveryDetail,
          addressCount: userData.address.length,
          addressIndices: userData.address.map((_, idx) => idx)
        });
        return openNotificationWithIcon(
          "error",
          t("buttons.set_a_delivery__1", lang) || "Please select a delivery address"
        );
      }

      // Validate that the selected address exists and has required fields
      const selectedAddress = userData.address[deliveryDetail];
      if (!selectedAddress) {
        logger.warn("⚠️ Selected address is null or undefined", {
          deliveryDetail,
          addressCount: userData.address.length,
          selectedAddress
        });
        return openNotificationWithIcon(
          "error",
          t("buttons.set_a_delivery__1", lang) || "Please select a valid delivery address"
        );
      }

      // Validate selected delivery address before proceeding (only on submit)
      // Only required for chefs (who use hubs), not for restaurants
      
      logger.log("?? SELECTED CHEF VALIDATION DEBUG:", {
        hasSelectedChef: !!selectedChef,
        chefId: selectedChef?._id,
        chefName: selectedChef ? `${selectedChef.firstname} ${selectedChef.lastname}` : 'No chef',
        isRestaurant: selectedChef?.restaurant,
        isChef: selectedChef?.chef,
        hasSelectedHub: !!selectedChef.selectedHub,
        selectedHubValue: selectedChef.selectedHub,
        selectedHubType: typeof selectedChef.selectedHub,
        allChefKeys: selectedChef ? Object.keys(selectedChef) : [],
        rawSelectedChef: selectedChef
      });
      
      // Log warning if chef has no selectedHub and is not a restaurant
      if (!selectedChef.selectedHub && !selectedChef.restaurant) {
        logger.warn("⚠️ Chef has no selectedHub:", {
          chefId: selectedChef._id,
          chefName: `${selectedChef.firstname} ${selectedChef.lastname}`,
          hasSelectedHub: !!selectedChef.selectedHub
        });
      }
      
      // Hub validation: Check if chef has hub assignment
      if (!selectedChef.restaurant && !selectedChef.selectedHub) {
        logger.error("? HUB VALIDATION FAILED:", {
          chefId: selectedChef?._id,
          chefName: `${selectedChef?.firstname} ${selectedChef?.lastname}`,
          isRestaurant: selectedChef?.restaurant,
          isChef: selectedChef?.chef,
          hasSelectedHub: !!selectedChef.selectedHub,
          selectedHub: selectedChef.selectedHub,
          validationLogic: {
            isRestaurant: !!selectedChef?.restaurant,
            hasSelectedHub: !!selectedChef.selectedHub,
            shouldPassValidation: !!(selectedChef?.restaurant || selectedChef.selectedHub)
          },
          troubleshooting: {
            chefType: selectedChef?.restaurant ? 'Restaurant' : selectedChef?.chef ? 'Chef' : 'Unknown',
            expectedBehavior: selectedChef?.restaurant ? 'Restaurant should not need hub' : 'Chef should have selectedHub populated'
          }
        });
        
        return openNotificationWithIcon(
          "error",
          t("errors.hub_not_assigned", lang) ||
            "This chef is not assigned to a delivery hub. Please contact support or try a different chef."
        );
      }
      
      logger.log("? Hub validation passed for:", {
        chefId: selectedChef._id,
        chefName: `${selectedChef.firstname} ${selectedChef.lastname}`,
        type: selectedChef.restaurant ? 'Restaurant' : 'Chef with Hub',
        hubName: selectedChef.selectedHub?.name
      });

      const selectedAddressValidation = validateAddress(deliveryDetail,storedData);
      if (selectedAddressValidation && !selectedAddressValidation.isValid) {
        const errorMessage =
          translateErrorMessage(
            selectedAddressValidation.error,
            lang,
            userData?.restaurant
          ) ||
          t("errors.delivery_address_too_far", lang)
            .replace(
              "${maxDistance}",
              selectedAddressValidation.maxDeliveryDistance ||
                MAX_DELIVERY_DISTANCE
            )
            .replace(
              "${distance}",
              selectedAddressValidation.distanceInKm || "N/A"
            );
        return openNotificationWithIcon("error", errorMessage);
      }

      setLoad(true);

      // First create the order
      const request_details = {
        type: POST,
        url: service_api,
        route: `order/create-order`,
        data: {
          food: cart,
          billing_details: {
            amount: cartTotal - promoValueState,
            delivery_price: deliveryFeeState,
            delivery_date: selectedWhen,
            tip: tip,
            tax: tax,
            service_fee: serviceFee,
            subtotal: cartTotal,
            promo_discount: promoValueState,
            total_without_promo: total,
            total_with_promo: totalWithPromo
          },
          delivery_details: {
            ...userData?.address[deliveryDetail],
            // Ensure position is properly formatted for the backend
            position: userData?.address[deliveryDetail]?.position || {
              lat: userData?.address[deliveryDetail]?.lat,
              lng: userData?.address[deliveryDetail]?.lng
            }
          },
          user_id: userData?._id,
          chef_id: cart[0].chef_id,
          discount_code: parseFloat(promo_value) === 0 ? null : promo_code
        },
        token: refreshToken
      };

      const response = await MakeRequestAsync(request_details);
      let encrypted = decryptSingleData(response.data.order_id);

      // Now create payment link and open in new tab
      const paymentLinkResponse = await fetch(
        `${service_api}/stripe/create-payment-link`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: refreshToken,
            session: process.env.REACT_APP_API_KEY
          },
          body: JSON.stringify({
            amount: Math.round(totalWithPromo * 100), // Convert to cents
            order_id: encrypted,
            success_url: `${window.location.origin}/order/sucessful/complete?order_id=${encrypted}`,
            cancel_url: `${window.location.origin}/cart?chef=${cart[0].chef_id}`,
            metadata: {
              order_id: encrypted,
              user_id: userData?._id
            }
          })
        }
      );

      const paymentData = await paymentLinkResponse.json();

      if (paymentData.success && paymentData.payment_link_url) {
        // Open payment link in new tab
        window.open(paymentData.payment_link_url, "_blank");
      } else {
        openNotificationWithIcon("error", t("errors.payment_link_error", lang));
      }
      
      setLoad(false);
    } catch (error) {
      
      // Handle selectedChef validation errors
      if (error.message.includes("selectedChef")) {
        logger.error("❌ CHECKOUT FAILED: selectedChef is missing", {
          error: error.message,
          selectedChef: selectedChef // Keep original selector for error logging context
        });
        return openNotificationWithIcon(
          "error",
          t("errors.chef_not_selected", lang) ||
            "Chef information is missing. Please return to the menu and try again."
        );
      }
      
      // Handle payment/order creation errors
      logger.error("❌ CHECKOUT ERROR:", {
        error: error,
        message: error?.response?.data?.message,
        status: error?.response?.status,
        isJWTError: error?.response?.data?.message?.includes('jwt') || error?.response?.data?.message?.includes('token')
      });

      // Handle specific JWT errors
      if (error?.response?.data?.message?.includes('jwt') || 
          error?.response?.data?.message?.includes('token') ||
          error?.response?.status === 401) {
        return openNotificationWithIcon(
          "error",
          "Your session has expired. Please refresh the page and login again."
        );
      }

      // Display the backend error message if available, otherwise show generic error
      const backendMessage = error?.response?.data?.message;
      const errorMessage = backendMessage
        ? translateErrorMessage(backendMessage, lang, userData?.restaurant)
        : (error.message || t("errors.order_start_error", lang));
      
      return openNotificationWithIcon("error", errorMessage);
    } finally {
      setLoad(false);
      isProcessingRef.current = false;
    }
  };

  // Debounced checkout handler
  // Note: loadPaymentPage is called directly (not in dependencies) to avoid stale closures
  // The function accesses current props/state via closure, so we intentionally exclude it
  const handleCheckout = useCallback(() => {
    // Clear any existing timeout
    if (checkoutDebounceRef.current) {
      clearTimeout(checkoutDebounceRef.current);
    }

    // Prevent if already processing or loading
    if (isProcessingRef.current || load || props?.loadback) {
      logger.debug("[WAIT] Checkout prevented: already processing or loading", {
        isProcessing: isProcessingRef.current,
        load,
        loadback: props?.loadback
      });
      return;
    }

    // Set debounce timeout - capture current values at execution time
    // Log the current deliveryDetail value at debounce time
    const currentDeliveryDetail = deliveryDetail;
    logger.debug("[TIMER] Debouncing checkout - current values:", {
      deliveryDetail: currentDeliveryDetail,
      deliveryDetailType: typeof currentDeliveryDetail,
      addressCount: userData?.address?.length || 0
    });
    
    checkoutDebounceRef.current = setTimeout(async () => {
      try {
        // Log values when debounced function executes
        logger.debug("[EXEC] Executing debounced checkout:", {
          deliveryDetail,
          deliveryDetailType: typeof deliveryDetail,
          addressCount: userData?.address?.length || 0
        });
        // Call loadPaymentPage which will access current props/state via closure
        await loadPaymentPage();
      } catch (error) {
        logger.error("❌ Checkout error caught in onClick handler:", error);
        openNotificationWithIcon(
          "error",
          error.message || t("errors.checkout_error", lang) || "An error occurred during checkout. Please try again."
        );
      } finally {
        checkoutDebounceRef.current = null;
      }
    }, 300); // 300ms debounce delay
  }, [load, props?.loadback, lang, t, deliveryDetail, userData, selectedChef]);

  // Address handling callback - called when AddressModal successfully adds an address
  const handleAddressAdded = (updatedUserData) => {
    logger.log("✅ Address successfully added via AddressModal:", {
      addressCount: updatedUserData?.address?.length,
      selectedChefStillExists: !!selectedChef,
      selectedChefId: selectedChef?._id
    });
    
    // selectedChef remains unchanged because Redux updates happened in isolated AddressModal
    // No need to refresh anything - AddressModal already updated userData in Redux
  };

  const addressUpdate = async (number) => {
    // Deleting addresses requires authentication - check and fetch token if needed
    const refreshToken = await getRefreshToken(userData, post_user_data);
    
    if (!userData?._id || !refreshToken) {
      return openNotificationWithIcon(
        "error",
        t("errors.login_required_manage_addresses", lang) ||
          "Please login using the navigation menu to manage saved addresses."
      );
    }

    setLoad(true);

    const request_details = {
      type: UPDATE,
      url: service_api,
      route: `user/update-user/${userData._id}`,
      data: {
        address: userData?.address?.filter((item, index) => {
          return index !== number;
        })
      },
      token: refreshToken
    };

    try {
      const response = await MakeRequestAsync(request_details);
      const decryptedData = decryptSingleData(response.data.user);
      post_user_data({
        ...decryptedData
      });
      openNotificationWithIcon("success", t("success.address_removed", lang));
    } catch (err) {
      logger.error("?? Address Delete Error:", {
        error: err,
        message: err?.response?.data?.message,
        status: err?.response?.status,
        isJWTError: err?.response?.data?.message?.includes('jwt') || err?.response?.data?.message?.includes('token')
      });

      logger.log(
        t("errors.fetch_data_error", lang),
        err?.response?.data?.message
      );

      // Handle specific JWT errors
      if (err?.response?.data?.message?.includes('jwt') || 
          err?.response?.data?.message?.includes('token') ||
          err?.response?.status === 401) {
        return openNotificationWithIcon(
          "error",
          "Your session has expired. Please refresh the page and login again."
        );
      }

      return openNotificationWithIcon(
        "error",
        err?.response?.data?.message || t("errors.profile_update_error", lang)
      );
    } finally {
      setLoad(false);
    }
  };

  const calculateTotal = () => {
    return total.toFixed(2);
  };

  const calculateTotalWithPromo = () => {
    return totalWithPromo.toFixed(2);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (checkoutDebounceRef.current) {
        clearTimeout(checkoutDebounceRef.current);
      }
    };
  }, []);

  return (
    <div className="section-cart">
      <div className="section-cart-block">
        <div className="section-cart-left">
          {/* Cart Header */}
          <h3
            className="section-cart-header-back"
            onClick={() => {
              return history.goBack();
            }}
          >
            {" "}
            <MdOutlineArrowBackIosNew fontSize={20} />{" "}
            {t("section_cart.go_back_chef_menu", lang, userData?.restaurant)}
          </h3>
          <h3 className="section-cart-header-title">
            {t("section_cart.delivery_information", lang)}
          </h3>
          <div className="section-cart-content-wrapper">
            <h3 className="section-cart-header-subtitle">
              {t("section_cart.address", lang)}
            </h3>
            <div className="input-block">
              <div
                className="btn-get-started section-cart-add-address-btn"
                onClick={() => {
                  if (load) {
                    return;
                  }

                  if (userData?.address?.length === 5) {
                    return openNotificationWithIcon(
                      "error",
                      t("buttons.max_address_is__1", lang)
                    );
                  }

                  setOpen(true);
                }}
              >
                {!load && t("buttons.add_2", lang)}
                {load && <CircularProgress size={25} />}
              </div>
              {userData?.address?.map((item, index) => {
                return (
                  <AddressItem
                    key={index}
                    item={item}
                    isSelected={index === deliveryDetail}
                    onSelect={() => setDeliveryDetail(index)}
                    onDelete={() => addressUpdate(index)}
                  />
                );
              })}
            </div>
            <h3 className="section-cart-header-subtitle">
              {t("section_cart.promo_gift_card", lang)}
            </h3>
            <div className="input-block section-cart-promo-wrapper">
              <label>{t("section_cart.coupon_code", lang)}</label>
              <div className="section-cart-promo-input-wrapper">
                <input
                  name="promo_code"
                  value={promo_code}
                  type="text"
                  placeholder={t("common.enter_promo_cod", lang)}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                  }}
                  className="section-cart-promo-input"
                />
                <div
                  className="btn-see-cart section-cart-promo-apply-btn"
                  disabled={promo_value !== 0}
                  onClick={() => {
                    if (promo_value !== 0) {
                      return;
                    }
                    getDiscount(promo_code);
                  }}
                >
                  {load && <CircularProgress size={25} />}
                  {!load && (
                    <span className="section-cart-promo-apply-text">
                      {promo_value === 0 && t("section_cart.apply", lang)}
                      {promo_value !== 0 &&
                        "$" + promo_value + t("common.discount", lang)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <AddressModal
              isOpen={open}
              onClose={() => setOpen(false)}
              onAddressAdded={handleAddressAdded}
            />
          </div>
          <div className="section-cart-spacer"></div>
        </div>
        <div className="section-cart-right">
          <h3 className="section-cart-right-header">
            {t("section_cart.your_orders_at", lang)}
            {" " +
              Aa(selectedChef?.firstname) +
              " " +
              Aa(selectedChef?.lastname)}
          </h3>
          <p className="section-cart-right-subtitle">
            {t("section_cart.delivery_for", lang)} {currentDate}
          </p>

          {cart.length > 0 && (
            <div className="section-cart-items-wrapper">
              {cart.map((item) => (
                <div className="section-cart-right-cart-btn">
                  <span
                    className="section-cart-right-cart-btn-span"
                    onClick={() => {
                      const result = decreaseQuantity(item._id);
                      // If cart becomes empty, redirect to chef's page
                      // Only redirect if selectedChef exists to prevent navigation to undefined route
                      if (result.isEmpty && selectedChef?._id) {
                        // Redirect to chef's page when cart becomes empty
                        history.push(`/chef/${selectedChef._id}`);
                      } else if (result.isEmpty && !selectedChef?._id) {
                        logger.warn("⚠️ Cannot redirect: selectedChef is undefined when cart becomes empty");
                      }
                    }}
                  >
                    -
                  </span>
                  <span>{item?.qty}</span>
                  <span
                    className="section-cart-right-cart-btn-span"
                    onClick={() => {
                      increaseQuantity(item._id);
                    }}
                  >
                    +
                  </span>
                  <span className="section-cart-item-name">
                    {item?.name?.slice(0, 25) + "..."}
                  </span>
                  <span className="section-cart-item-price">
                    {"$" + (item?.cost * item?.qty)?.toFixed(2)}
                  </span>
                </div>
              ))}
              {/* New Tip Section */}
              <div className="section-cart-tip-wrapper">
                <div className="section-cart-divider" />

                {/* Charity Contribution Section */}
                <ContributionComponent
                  lang={lang}
                  selectedContribution={props?.selectedTip}
                  setSelectedContribution={props?.setSelectedTip}
                  customAmount={custom_help}
                  setCustomAmount={setCustomHelp}
                  cartTotal={cartTotal}
                  showInfoIcon={true}
                  context="charity"
                />
              </div>

              <div className="section-cart-divider-bottom" />

              <div className="section-cart-summary">
                <div className="section-cart-summary-item">
                  <span className="section-cart-summary-label">
                    {t("common.subtotal", lang)}
                  </span>
                  <span className="section-cart-summary-value">
                    {" $" + (cartTotal + tip - promoValueState).toFixed(2)}
                  </span>
                </div>
                <div className="section-cart-summary-item">
                  <span className="section-cart-summary-label">
                    {t("common.delivery_fee_2", lang)}
                  </span>
                  <span className="section-cart-summary-value">
                    {" $" + deliveryFeeState.toFixed(2)}
                  </span>
                </div>
                {/* Tip line item */}
                {tip > 0 && (
                  <div className="section-cart-summary-item">
                    <span className="section-cart-summary-label">
                       {t("charity_section.title", lang).replace("?? ", "")}
                    </span>
                    <span className="section-cart-summary-value section-cart-summary-value--charity">
                      {" +$" + tip.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="section-cart-summary-item">
                  <span className="section-cart-summary-label">
                    {t("common.service_fee", lang)}
                  </span>
                  <span className="section-cart-summary-value">
                    {" $" + serviceFee.toFixed(2)}
                  </span>
                </div>
                <div className="section-cart-summary-item">
                  <span className="section-cart-summary-label">
                    {t("common.tax_other_fees", lang)}
                  </span>
                  <span className="section-cart-summary-value">
                    {" $" + tax.toFixed(2)}
                  </span>
                </div>
              </div>
              <div
                className="btn-see-cart section-cart-checkout-btn"
                disabled={load || props?.loadback}
                onClick={handleCheckout}
              >
                {(props?.loadback || load) && <CircularProgress size={25} />}
                {!load && !props?.loadback && (
                  <>
                    <span className="section-cart-checkout-text">
                      {t("section_cart.checkout", lang)}
                    </span>
                    <span
                      className={`section-cart-checkout-price ${
                        promo_value !== 0
                          ? "section-cart-checkout-price--strikethrough"
                          : ""
                      }`}
                    >
                      {"($" + calculateTotal() + ")"}
                    </span>
                    {promo_value !== 0 && (
                      <span className="section-cart-checkout-price--promo">
                        {" $" + calculateTotalWithPromo()}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Additional checkout information */}
              <div className="section-cart-additional-info"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SectionCart);

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});
